import { initOtel } from '@/observability/otel';

// Start OpenTelemetry before anything else so document-load + early fetch spans are captured.
// No-op unless the AppHost injected an OTLP endpoint (local dev only).
initOtel();

import { computed, createApp } from 'vue';
import { createPinia } from 'pinia';
import { useAuthStore } from './stores/auth';
import { useOrganizationStore } from './stores/organization';
import { useAdminStore } from './stores/admin';
import { useLMSStore } from './stores/lms';
import '@/assets/styles/default.css';
import PrimeVue from 'primevue/config';
import { primeVueConfig } from '@/config/primevue';
import App from './App.vue';
import router from './router';
import adminRouter from './router/admin-router';
import lmsRouter from '@/router/lms-router';
import { useNotificationStore } from './stores/notification';
import { useThemeStore } from './stores/theme';
import { enableClipboardFilePasteForFileInputs } from '@/lib/file-input-paste';
import { installStaleChunkReload } from '@/lib/stale-chunk-reload';
import { getDefaultAdminRouteName, getInitialAdminModeState } from '@/router/admin-access';
import { hasEffectiveRole } from '@/lib/effective-roles';

const app = createApp(App).use(createPinia());
app.use(PrimeVue, primeVueConfig);

const authStore = useAuthStore();
const organizationStore = useOrganizationStore();
const lmsStore = useLMSStore();
const adminStore = useAdminStore();
useThemeStore();

const initialAdminModeState = getInitialAdminModeState({
    isAdminModeEnabled: localStorage.getItem('admin') === 'true',
    currentPath: window.location.pathname,
    roleContext: adminStore.adminRoleContext,
});

localStorage.setItem('admin', initialAdminModeState.isAdminModeEnabled.toString());

if (initialAdminModeState.redirectPath !== undefined) {
    window.history.replaceState(window.history.state, '', initialAdminModeState.redirectPath);
}

function buildLoginRedirect(to: { fullPath: string; query: Record<string, unknown> }) {
    return {
        name: 'login',
        query: {
            ...to.query,
            redirect: to.fullPath,
        },
    };
}

// navigation guards
router.beforeEach((to, from, next) => {
    if (to.matched.some((r) => r.meta.requiresAuth) && !authStore.isAuthenticated) {
        return next(buildLoginRedirect(to));
    } else if (!to.matched.some((r) => r.meta.requiresAuth)) {
        // Routes like /users live in the admin router, which is only mounted after login.
        // The org router's catch-all matches them instead, with no requiresAuth, so the
        // guard above never fires and no redirect query param is set. Save the path here
        // so finalizeLogin can navigate there once the correct router is active.
        if (to.name === 'not-found' && !authStore.isAuthenticated) {
            sessionStorage.setItem('pendingRedirect', to.fullPath);
            return next({ name: 'login' });
        }
        return next();
    }

    // Redirect to loading if roles are not loaded yet
    if (!organizationStore.user && !['/loading', '/', '/login'].includes(to.path)) {
        return next({
            path: '/loading',
            query: { redirect: to.fullPath },
        });
    }

    //TODO: add in the custom some data check
    // Flatten all allowed roles into one array
    const allowedRoles = to.matched
        .map((r) => r.meta.allowedRoles)
        .filter(Boolean)
        .flat() as string[];

    const requiresSomeDataAccess = to.matched.some((record) => record.meta.requiresSomeDataAccess);

    if (allowedRoles.length > 0 || requiresSomeDataAccess) {
        const hasRole = hasEffectiveRole(
            organizationStore.currentUserRoles,
            organizationStore.currentOrgRoles,
            allowedRoles,
        );

        if (!hasRole) {
            // const requiresSomeDataAccess = to.matched.some((record) => record.meta.requiresSomeDataAccess);
            if (requiresSomeDataAccess) {
                const hasSomeDataAccess = organizationStore.hasDataAccess;
                if (hasSomeDataAccess) {
                    return next();
                }
            }
            organizationStore.setToastMessage(`You do not have permission to access ${String(to.name)}`, 'warning');
            return next('/');
        }
    }

    next();
});

adminRouter.beforeEach((to, from, next) => {
    if (to.matched.some((r) => r.meta.requiresAuth) && !authStore.isAuthenticated) {
        return next(buildLoginRedirect(to));
    }

    const roleContext = adminStore.adminRoleContext;
    const { userCPRoles, organizationSchemeRoles } = roleContext;
    // Authorization logic
    const hasNotificationAccess = () => useNotificationStore().hasNotificationAccess;

    // Redirect to loading if roles are not loaded yet
    if (
        userCPRoles.length === 0 &&
        organizationSchemeRoles.length === 0 &&
        !['/loading', '/', '/login'].includes(to.path)
    ) {
        return next({
            path: '/loading',
            query: { redirect: to.fullPath },
        });
    }

    const allowedSystemRoles = to.matched
        .map((r) => r.meta.allowedSystemRoles)
        .filter(Boolean)
        .flat() as string[];

    const requiredOrganizationSchemeRoles = to.matched
        .map((record) => record.meta.requiredOrganizationSchemeRoles)
        .filter(Boolean)
        .flat() as string[];

    if (
        requiredOrganizationSchemeRoles.length > 0 &&
        !requiredOrganizationSchemeRoles.some((role) => organizationSchemeRoles.includes(role))
    ) {
        adminStore.setToastMessage(`You do not have permission to access ${String(to.name)}`, 'warning');
        const fallbackAdminRouteName = getDefaultAdminRouteName(roleContext);
        if (fallbackAdminRouteName && fallbackAdminRouteName !== to.name) {
            return next({ name: fallbackAdminRouteName });
        }

        return next(false);
    }

    if (allowedSystemRoles.length > 0) {
        const isAdmin = userCPRoles.includes('admin');
        const hasRole = allowedSystemRoles.some((role) => userCPRoles.includes(role));

        if (!hasRole && !isAdmin) {
            const needsNotificationAccess = to.matched.some((record) => record.meta.hasNotificationAccess);
            if (needsNotificationAccess && hasNotificationAccess()) {
                return next();
            }

            adminStore.setToastMessage(`You do not have permission to access ${String(to.name)}`, 'warning');
            const fallbackAdminRouteName = getDefaultAdminRouteName(roleContext);
            if (fallbackAdminRouteName && fallbackAdminRouteName !== to.name) {
                return next({ name: fallbackAdminRouteName });
            }

            return next('/');
        }
    }
    next();
});

lmsRouter.beforeEach((to, from, next) => {
    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth);

    if (requiresAuth && !authStore.isAuthenticated) {
        return next(buildLoginRedirect(to));
    }

    return next();
});

// wrap in check to localStorage whet
// her to use adminRouter or organizationRouter

const selectedRouter = computed(() =>
    lmsStore.isLMS ? lmsRouter : JSON.parse(localStorage.getItem('admin') ?? 'false') ? adminRouter : router,
);

app.use(selectedRouter.value);
enableClipboardFilePasteForFileInputs();
installStaleChunkReload([router, adminRouter, lmsRouter]);
app.mount('#app');

console.log('atNorth Service Portal');
